import { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

const getMarkdownTokens = (theme: Theme) => {
  const isDark = theme.palette.mode === 'dark';

  return {
    surface: isDark ? theme.palette.grey[200] : theme.palette.surface.sunken,
    heading: theme.palette.text.primary,
    body: isDark ? theme.palette.text.secondary : theme.palette.extendedGrey.dark,
    border: isDark ? theme.palette.divider : theme.palette.border.default,
    codeBg: isDark ? theme.palette.info.light : theme.palette.primary.light,
    codeColor: isDark ? theme.palette.primary.main : theme.palette.primary.dark,
    blockquoteBg: isDark ? theme.palette.action.hover : theme.palette.surface.card,
    tableBg: isDark ? theme.palette.grey[100] : theme.palette.surface.card,
    tableHeaderBg: isDark ? theme.palette.info.light : theme.palette.primary.light,
    preBg: isDark ? theme.palette.background.default : theme.palette.extendedGrey.darker,
    preCodeColor: isDark ? theme.palette.text.primary : theme.palette.primary.light,
    link: theme.palette.primary.main,
    linkHover: theme.palette.primary.dark,
  };
};

export const getMarkdownContentStyles = (
  theme: Theme,
  options?: { minHeight?: string; variant?: 'default' | 'embedded' },
): SxProps<Theme> => {
  const tokens = getMarkdownTokens(theme);
  const isEmbedded = options?.variant === 'embedded';

  return {
    backgroundColor: isEmbedded ? 'transparent' : tokens.surface,
    color: tokens.heading,
    padding: isEmbedded ? 0 : '16px',
    borderRadius: isEmbedded ? 0 : '4px',
    wordBreak: 'break-word',
    fontSize: '1rem',
    lineHeight: 1.6,
    ...(options?.minHeight ? { minHeight: options.minHeight } : {}),

    '& h1, & h2, & h3, & h4, & h5, & h6': {
      color: tokens.heading,
      marginTop: '24px',
      marginBottom: '16px',
      fontWeight: 'bold',
    },
    '& h1': {
      fontSize: '2em',
      borderBottom: `2px solid ${tokens.link}`,
      paddingBottom: '0.3em',
    },
    '& h2': {
      fontSize: '1.5em',
      borderBottom: `1px solid ${tokens.border}`,
      paddingBottom: '0.3em',
    },
    '& h3': {
      fontSize: '1.25em',
    },

    '& p': {
      marginBottom: '16px',
      lineHeight: '1.6',
      color: tokens.body,
    },

    '& ul, & ol': {
      paddingLeft: '2em',
      marginBottom: '16px',
      color: tokens.body,
    },
    '& li': {
      marginBottom: '8px',
    },

    '& code': {
      backgroundColor: tokens.codeBg,
      color: tokens.codeColor,
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '0.9em',
      fontFamily: 'monospace',
      border: `1px solid ${tokens.border}`,
    },
    '& pre': {
      backgroundColor: tokens.preBg,
      padding: '16px',
      borderRadius: '6px',
      overflow: 'auto',
      marginBottom: '16px',
      border: `1px solid ${tokens.border}`,
      '& code': {
        backgroundColor: 'transparent',
        color: tokens.preCodeColor,
        padding: 0,
        border: 'none',
      },
    },

    '& blockquote': {
      borderLeft: `4px solid ${tokens.link}`,
      marginLeft: 0,
      marginRight: 0,
      color: tokens.body,
      fontStyle: 'italic',
      marginBottom: '16px',
      backgroundColor: tokens.blockquoteBg,
      padding: '12px 16px',
      borderRadius: '4px',
    },

    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      marginBottom: '16px',
      backgroundColor: tokens.tableBg,
      borderRadius: '6px',
      overflow: 'hidden',
    },
    '& th, & td': {
      border: `1px solid ${tokens.border}`,
      padding: '10px 14px',
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: tokens.tableHeaderBg,
      color: tokens.heading,
      fontWeight: 'bold',
    },
    '& td': {
      backgroundColor: tokens.tableBg,
      color: tokens.body,
    },

    '& a': {
      color: tokens.link,
      textDecoration: 'none',
      fontWeight: 500,
      '&:hover': {
        textDecoration: 'underline',
        color: tokens.linkHover,
      },
    },

    '& hr': {
      border: 'none',
      borderTop: `2px solid ${tokens.border}`,
      margin: '24px 0',
    },

    '& strong': {
      fontWeight: 'bold',
      color: tokens.heading,
    },
    '& em': {
      fontStyle: 'italic',
      color: tokens.body,
    },
    '& s, & del': {
      textDecoration: 'line-through',
      color: tokens.body,
    },
  };
};

export const getEditorContainerStyles = (theme: Theme, error: boolean): SxProps<Theme> => {
  const isDark = theme.palette.mode === 'dark';
  const tokens = getMarkdownTokens(theme);
  const defaultBorder = isDark ? theme.palette.divider : alpha(theme.palette.common.black, 0.23);
  const hoverBorder = isDark
    ? theme.palette.text.secondary
    : alpha(theme.palette.common.black, 0.87);

  return {
    border: error ? '1px solid' : `1px solid ${defaultBorder}`,
    borderColor: error ? 'error.main' : defaultBorder,
    borderRadius: '4px',
    overflow: 'hidden',
    '&:hover': {
      borderColor: error ? 'error.main' : hoverBorder,
    },
    '&:focus-within': {
      borderColor: error ? 'error.main' : 'primary.main',
      borderWidth: '2px',
    },

    '& .rc-md-editor': {
      border: 'none',
      borderRadius: '0',
      backgroundColor: tokens.surface,
      color: tokens.heading,
    },

    '& .rc-md-navigation': {
      backgroundColor: isDark ? theme.palette.grey[100] : theme.palette.grey.A100,
      borderBottom: `1px solid ${tokens.border}`,
      padding: '8px',
      minHeight: '48px',
    },

    '& .button-wrap button': {
      fontSize: '16px',
      padding: '4px 8px',
      margin: '0 2px',
      border: '1px solid transparent',
      borderRadius: '4px',
      cursor: 'pointer',
      backgroundColor: 'transparent',
      color: isDark ? theme.palette.text.secondary : alpha(theme.palette.common.black, 0.6),
      transition: 'all 0.2s',
      '&:hover': {
        backgroundColor: isDark
          ? theme.palette.action.hover
          : alpha(theme.palette.common.black, 0.04),
        color: isDark ? theme.palette.text.primary : alpha(theme.palette.common.black, 0.87),
      },
    },

    '& .editor-container': {
      fontSize: '14px',
      lineHeight: '1.6',
      backgroundColor: tokens.surface,
      color: tokens.heading,
    },

    '& .sec-md': {
      padding: '16px',
      backgroundColor: tokens.surface,
      color: tokens.heading,
    },

    '& .sec-md .input': {
      backgroundColor: `${tokens.surface} !important`,
      color: `${tokens.heading} !important`,
      caretColor: tokens.heading,
    },

    '& .editor-container > .section': {
      borderColor: `${tokens.border} !important`,
    },

    '& .drop-wrap, & .header-list': {
      backgroundColor: isDark ? theme.palette.grey[100] : theme.palette.surface.card,
      color: tokens.heading,
      borderColor: tokens.border,
    },

    '& textarea': {
      backgroundColor: `${tokens.surface} !important`,
      color: `${tokens.heading} !important`,
    },

    '& .custom-html-style': getMarkdownContentStyles(theme),
  } as SxProps<Theme>;
};

export const getLabelStyles = (error: boolean): SxProps<Theme> => ({
  mb: 1,
  color: error ? 'error.main' : 'text.secondary',
  fontWeight: 500,
});

export const getHelperTextStyles = (): SxProps<Theme> => ({
  ml: 1.75,
  mt: 0.75,
});
